import { useState } from 'react';
import {
  Box,
  Button,
  makeStyles,
  createStyles,
  Grid,
  Collapse,
  Checkbox,
  FormControl,
  FormControlLabel,
} from '@material-ui/core';
import { colors, DefaultButton } from 'design-system';

// data layer
import { mapToCatalogItem } from 'data-layer-cr/dist/functions/catalogs';
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';

// helpers function date
import { format, set, addDays } from 'date-fns';

// models
import {
  FilterDate,
  FilterOptionDate,
  FilterOptionLicense,
} from 'src/models/FilterMetric';

// lodash
import { uniqueId } from 'lodash';

// date format
import FORMAT_QUERY_STRING from 'src/constants/dateFormatQueryString';

// icons
import { FilterIcon } from 'src/components/Icons';

// components
import DateRangePicker from 'src/components/DateRangePicker';
import { DropdownCheckbox } from 'src/components/Inputs';

const useStyle = makeStyles(() =>
  createStyles({
    root: {
      border: `1px solid ${colors.gallery}`,
      borderRadius: 10,
      padding: 14,
    },
    selectedButton: {
      border: `1px solid ${colors.gallery}`,
      borderRadius: 10,
      color: 'white',
      background: colors.madison,
      textTransform: 'capitalize',
      fontWeight: 500,
      fontSize: '15px',
      padding: '15px 27px',
      '&:hover': {
        color: 'white',
        background: colors.madison,
      },
    },
    startIconFilterRangeDateButton: {
      fill: colors.bright_turquoise,
      '&:hover': {
        fill: 'white',
      },
    },
    butttonDate: {
      border: `1px solid ${colors.gallery}`,
      textTransform: 'capitalize',
      fontWeight: 500,
      fontSize: '15px',
      color: colors.madison,
      padding: '15px 27px',
      '&:hover': {
        color: 'white',
        borderRadius: 10,
        background: colors.madison,
      },
    },
  }),
);

type FilterGroupProps = {
  filterDateList: FilterDate;
  handleSelectDateFilter: (newSelectedDate: FilterOptionDate) => void;
  filterDateSelected: FilterOptionDate;
  handleSelectLicenseFilter: (
    newFilterOptionLicense: FilterOptionLicense,
  ) => void;
  filterLicenseSelected: FilterOptionLicense;
  licenses: GeneralCatalogItem[];
  handleSelectSubagentFilter: (
    newFilterOptionSubagent: FilterOptionLicense,
  ) => void;
  filterSubagentSelected: FilterOptionLicense;
  subAgentList: GeneralCatalogItem[];
  handleCheckCurrentUserFilter: (isChecked: boolean) => void;
  isCheckedCurrentUserFilter: boolean;
  userName: string | undefined;
  handleResetFilters: () => void;
  isQueriableSubagent: boolean;
};

export const FilterGroup = ({
  filterDateList,
  handleSelectDateFilter,
  filterDateSelected: {
    id: selectedDateId,
    description: selectedDescriptionDate,
  },
  handleSelectLicenseFilter,
  filterLicenseSelected,
  licenses,
  handleCheckCurrentUserFilter,
  isCheckedCurrentUserFilter,
  userName,
  handleResetFilters,
  subAgentList,
  filterSubagentSelected,
  handleSelectSubagentFilter,
  isQueriableSubagent,
}: FilterGroupProps): JSX.Element => {
  const [showFilter, setShowFilter] = useState(false);
  const classes = useStyle({ showFilter });

  const handleOnAddDateRangeFilter = (
    description: string,
    startDate: Date,
    endDate: Date,
  ) => {
    // set start date time to 05:00:00
    const startDateWithCustomTime = set(startDate, {
      hours: 5,
      minutes: 0,
      seconds: 0,
    });

    // set end date time to 05:00:00
    const endDateWithCustomTime = set(endDate, {
      hours: 5,
      minutes: 0,
      seconds: 0,
    });

    handleSelectDateFilter({
      id: 'range',
      description,
      startDate: format(startDateWithCustomTime, FORMAT_QUERY_STRING),
      endDate: format(addDays(endDateWithCustomTime, 1), FORMAT_QUERY_STRING),
    });
  };

  const handleOnSelectLicenseFilter = (
    selected: string,
    description: string,
  ) => {
    handleSelectLicenseFilter({ description, queryKey: selected });
  };

  const handleOnSelectSubagentFilter = (
    selected: string,
    description: string,
  ) => {
    handleSelectSubagentFilter({ description, queryKey: selected });
  };

  const isSelectedRangeDatePicker: boolean = selectedDateId === 'range';

  const currentSelectedLicense = (filterOption: FilterOptionLicense) => {
    if (!filterOption.queryKey) {
      return [];
    }
    if (filterOption.queryKey === 'default') {
      return [];
    }
    return filterOption.queryKey.split(',');
  };

  const filterDateListWithoutRange = Object.values(filterDateList).filter(
    ({ id }) => id !== 'range',
  );

  const showSubagentFilter = isQueriableSubagent && subAgentList.length > 0;

  return (
    <Grid container className={classes.root}>
      <Grid
        item
        container
        xs={12}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <Box display="flex">
            <DateRangePicker
              data-testid="range-date-picker"
              isSelected={isSelectedRangeDatePicker}
              onAddFilter={handleOnAddDateRangeFilter}
              buttonStartIconColor={
                isSelectedRangeDatePicker ? 'white' : undefined
              }
              dateToShowFormat="dd/MM/yyyy"
              buttonClassName={
                isSelectedRangeDatePicker
                  ? classes.selectedButton
                  : classes.butttonDate
              }
              descriptionWhenSelected={selectedDescriptionDate}
              descriptionWhenNotSelected="Personalizado"
            />
            {filterDateListWithoutRange.map(
              ({ description, endDate, id, startDate }) => (
                <Button
                  className={
                    id === selectedDateId
                      ? classes.selectedButton
                      : classes.butttonDate
                  }
                  key={uniqueId()}
                  data-testid={`button-${id}`}
                  onClick={() =>
                    handleSelectDateFilter({
                      description,
                      endDate,
                      id,
                      startDate,
                    })
                  }
                >
                  {description}
                </Button>
              ),
            )}
          </Box>
        </Grid>
        <Grid item>
          <Button
            data-testid="show-filters"
            style={{ color: colors.bright_turquoise }}
            startIcon={
              <FilterIcon
                strokeWidth="2px"
                color={colors.bright_turquoise}
                style={{ marginRight: 8 }}
              />
            }
            onClick={() => setShowFilter(!showFilter)}
          >
            {showFilter ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </Button>
        </Grid>
      </Grid>
      <Collapse
        in={showFilter}
        style={{ width: '100%' }}
        component="div"
        data-testid="seconed-line-filters-container"
      >
        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          style={{ paddingTop: 14 }}
        >
          <Grid item>
            <Grid container alignItems="center" style={{ gap: 14 }}>
              <Grid item>
                <DropdownCheckbox
                  data-testid="license-dropdown"
                  id="sales-web-dropdown-licenses"
                  optionList={mapToCatalogItem(licenses)}
                  applyChanges={handleOnSelectLicenseFilter}
                  currentValues={currentSelectedLicense(filterLicenseSelected)}
                  element="Licencias"
                />
              </Grid>
              {showSubagentFilter && (
                <Grid item>
                  <DropdownCheckbox
                    data-testid="subagent-dropdown"
                    id="sales-web-dropdown-subagents"
                    optionList={mapToCatalogItem(subAgentList)}
                    applyChanges={handleOnSelectSubagentFilter}
                    currentValues={currentSelectedLicense(
                      filterSubagentSelected,
                    )}
                    element="Subagentes"
                  />
                </Grid>
              )}
              <Grid item>
                <FormControl>
                  <FormControlLabel
                    control={<Checkbox style={{ marginRight: 9 }} />}
                    onChange={(_e, checked) => {
                      handleCheckCurrentUserFilter(checked);
                    }}
                    data-testid="checkbox-current-user"
                    name="user"
                    label={`Sólo las de: ${userName}`}
                    checked={isCheckedCurrentUserFilter}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <DefaultButton
              variant="outlined"
              onClick={handleResetFilters}
              data-testid="button-reset-filters"
            >
              Restablecer Filtros
            </DefaultButton>
          </Grid>
        </Grid>
      </Collapse>
    </Grid>
  );
};

export default FilterGroup;
